import React, { ReactNode, useCallback, useState } from 'react';

import noop from 'lodash/noop';
import { useSelector } from 'react-redux';

import { ConditionalContentRenderer } from '../../../../../../shared/components/conditional-content-renderer';
import Spin from '../../../../../../shared/components/spin';
import { ImageName } from '../../../../../../shared/components/svg-image/images';
import { selectIsCalendarSummarizationFeatureEnabled } from '../../../../../../shared/features/flags/selectors';
import { isMobileUa } from '../../../../../../shared/features/uatraits';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { selectIsPermissionsExtendedEnabled } from '../../../../selectors';
import { selectHasSummarizationPermission } from '../../../permissions';
import {
    selectIsSummarizationMailForParticipants,
    selectSummarizationStatus,
    showSummarizationMailRecipientToastAction,
    showSummarizationNoPermissionToStartToastAction,
    startSummarizationAction,
    stopSummarizationAction,
} from '../../../summarization';
import css from './styles.css';
import { SummarizationControlContent } from './summarization-control-content';

interface SummarizationControlPropsBase {
    title: string;
    onClick: () => void;
    iconName?: ImageName;
    icon?: ReactNode;
    disabled: boolean;
    content?: ReactNode;
    className?: string | null;
    tooltip?: string | null;
}

interface SummarizationControlProps extends SummarizationControlPropsBase {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

export function useSummarizationControlProps() {
    const hasSummarizationPermission = useSelector(selectHasSummarizationPermission);
    const isSummarizationMailForParticipants = useSelector(selectIsSummarizationMailForParticipants);
    const isMobile = useSelector(isMobileUa);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const summarizationStatus = useSelector(selectSummarizationStatus);
    const isPermissionsExtendedEnabled = useSelector(selectIsPermissionsExtendedEnabled);
    const isCalendarSummarizationFeatureEnabled = useSelector(selectIsCalendarSummarizationFeatureEnabled);
    const actions = useActions({
        start: startSummarizationAction,
        stop: stopSummarizationAction,
        showMailRecipientToast: showSummarizationMailRecipientToastAction,
        showNoPermissionToStartToast: showSummarizationNoPermissionToStartToastAction,
    });

    const startSummarization = useCallback(() => {
        actions.start();
        countMetrika('meeting_screen', 'toolbar', 'summarization-start');
    }, [actions]);
    const stopSummarization = useCallback(() => {
        actions.stop();
        countMetrika('meeting_screen', 'toolbar', 'summarization-stop');
    }, [actions]);

    const showMailRecipientToast = useCallback(() => {
        actions.showMailRecipientToast();
    }, [actions]);

    const showNoPermissionToStartToast = useCallback(() => {
        actions.showNoPermissionToStartToast();
    }, [actions]);

    const props: SummarizationControlProps = (() => {
        switch (summarizationStatus) {
            case 'STARTING':
                return {
                    title: i18n('toolbar', isMobile ? 'summarization-starting-extended' : 'summarization-starting'),
                    onClick: noop,
                    icon: <Spin size="md" color="white" progress />,
                    disabled: !hasSummarizationPermission,
                    content: i18n('toolbar', 'summarization-starting'),
                };
            case 'RUNNING':
                return {
                    title:
                        isMobile && hasSummarizationPermission
                            ? i18n('toolbar', 'summarization-stop-extended')
                            : i18n(
                                  'toolbar',
                                  isHovered && hasSummarizationPermission
                                      ? 'summarization-stop-v2'
                                      : 'summarization-running'
                              ),
                    onClick: hasSummarizationPermission ? stopSummarization : showMailRecipientToast,
                    iconName:
                        isHovered && !isMobile && hasSummarizationPermission ? 'recordStop' : 'summarizationInProgress',
                    disabled: !isMobile && !hasSummarizationPermission,
                    className: isMobile && !hasSummarizationPermission ? css.mimicDisabledButton : null,
                    tooltip:
                        !hasSummarizationPermission && !isMobile && !isPermissionsExtendedEnabled
                            ? i18n(
                                  'toolbar',
                                  isCalendarSummarizationFeatureEnabled
                                      ? 'summarization-running'
                                      : isSummarizationMailForParticipants
                                        ? 'summarization-mail-for-participants'
                                        : 'summarization-mail-for-admins'
                              )
                            : null,
                    content: hasSummarizationPermission ? (
                        <ConditionalContentRenderer
                            condition={isHovered}
                            content={
                                <SummarizationControlContent>
                                    {i18n('toolbar', 'summarization-stop-v2')}
                                </SummarizationControlContent>
                            }
                            fallback={
                                <SummarizationControlContent>
                                    {i18n('toolbar', 'summarization-running')}
                                </SummarizationControlContent>
                            }
                        />
                    ) : (
                        <SummarizationControlContent>
                            {i18n('toolbar', 'summarization-running')}
                        </SummarizationControlContent>
                    ),
                };
            default: {
                const tooltip = (() => {
                    if (isMobile) {
                        return null;
                    }

                    if (hasSummarizationPermission || isCalendarSummarizationFeatureEnabled) {
                        return i18n('toolbar', 'summarization-start-v2');
                    }

                    if (isPermissionsExtendedEnabled && !hasSummarizationPermission) {
                        return i18n('toolbar', 'organizer-revoked-summarization-permission');
                    }

                    return i18n('toolbar', 'summarization-no-permission-to-start');
                })();

                return {
                    tooltip: tooltip,
                    title: i18n('toolbar', 'summarization-start-v2'),
                    onClick: hasSummarizationPermission ? startSummarization : showNoPermissionToStartToast,
                    iconName: 'summarization',
                    disabled: !isMobile && !hasSummarizationPermission,
                    className: isMobile && !hasSummarizationPermission ? css.mimicDisabledButton : null,
                };
            }
        }
    })();

    const onMouseEnter = () => setIsHovered(true);
    const onMouseLeave = () => setIsHovered(false);

    return {
        ...props,
        onMouseEnter,
        onMouseLeave,
    };
}
